import React, { Fragment } from 'react';
import moment from 'moment';
import { t } from 'Src/i18n';
import { BucketFormikInput as Field } from 'Src/types/forms';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { useAppContext } from 'Context/ReactAppContext';
import { COMPANION_GROUPS } from './PrebookingForm.constants';
import type { FormFieldOption } from './PrebookingForm.utils';
import './PrebookingForm.utils';
type PrebookingFormCompanionProps = {
  civilities: Array<FormFieldOption>;
  companionGroup: 'adults' | 'kids';
  dateFormat: string;
  hasUnicodeFields: boolean;
  index: number;
  birthday?: string;
};
export const PrebookingFormCompanion = ({
  companionGroup,
  dateFormat,
  hasUnicodeFields,
  index,
  civilities,
  birthday,
}: PrebookingFormCompanionProps) => {
  const appContext = useAppContext();
  const showUnicodeFieldsFirst = isFeatureFlipped(appContext)('prebooking.unicodeFieldsFirst');
  const formHeader =
    companionGroup === COMPANION_GROUPS.ADULTS
      ? `${t('prebookingForm.pax.adultCompanionLabel')} ${index + 1}`
      : `${t('prebookingForm.pax.childCompanionLabel')} ${birthday ? moment(birthday).format(dateFormat) : ''}`;
  return (
    <div className="flex flex-wrap">
      <p className="mb-10 w-full px-10 text-14 font-bold">{formHeader}</p>
      <div className="w-full">
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId={`${companionGroup}-${index}-civility-field`}
            hideErrorMessage
            isRequired
            name={`${companionGroup}.${index}.civility`}
            options={civilities}
            placeholder={`${t('prebookingForm.mainTraveler.civilityPlaceholder')}*`}
            type="select"
          />
        </div>
      </div>
      {hasUnicodeFields && showUnicodeFieldsFirst && (
        <Fragment>
          <div className="w-full p-10 md:w-1/2">
            <Field
              dataTestId={`${companionGroup}-${index}-firstNameUnicode-field`}
              hideErrorMessage
              name={`${companionGroup}.${index}.firstNameUnicode`}
              optimized
              isRequired
              placeholder={t('prebookingForm.mainTraveler.firstNameUnicodePlaceholder')}
              type="text"
            />
          </div>
          <div className="w-full p-10 md:w-1/2">
            <Field
              dataTestId={`${companionGroup}-${index}-lastNameUnicode-field`}
              hideErrorMessage
              isRequired
              name={`${companionGroup}.${index}.lastNameUnicode`}
              optimized
              placeholder={t('prebookingForm.mainTraveler.lastNameUnicodePlaceholder')}
              type="text"
            />
          </div>
        </Fragment>
      )}
      <div className="w-full p-10 md:w-1/2">
        <Field
          dataTestId={`${companionGroup}-${index}-firstName-field`}
          hideErrorMessage
          isRequired
          name={`${companionGroup}.${index}.firstName`}
          optimized
          placeholder={`${t('prebookingForm.mainTraveler.firstNamePlaceholder')}*`}
          type="text"
        />
      </div>
      <div className="w-full p-10 md:w-1/2">
        <Field
          dataTestId={`${companionGroup}-${index}-lastName-field`}
          hideErrorMessage
          isRequired
          name={`${companionGroup}.${index}.lastName`}
          optimized
          placeholder={`${t('prebookingForm.mainTraveler.lastNamePlaceholder')}*`}
          type="text"
        />
      </div>
      {hasUnicodeFields && !showUnicodeFieldsFirst && (
        <Fragment>
          <div className="w-full p-10 md:w-1/2">
            <Field
              dataTestId={`${companionGroup}-${index}-firstNameUnicode-field`}
              hideErrorMessage
              name={`${companionGroup}.${index}.firstNameUnicode`}
              optimized
              isRequired
              placeholder={t('prebookingForm.mainTraveler.firstNameUnicodePlaceholder')}
              type="text"
            />
          </div>
          <div className="w-full p-10 md:w-1/2">
            <Field
              dataTestId={`${companionGroup}-${index}-lastNameUnicode-field`}
              hideErrorMessage
              isRequired
              name={`${companionGroup}.${index}.lastNameUnicode`}
              optimized
              placeholder={t('prebookingForm.mainTraveler.lastNameUnicodePlaceholder')}
              type="text"
            />
          </div>
        </Fragment>
      )}
      {companionGroup === COMPANION_GROUPS.ADULTS && (
        <div className="w-full p-10 md:w-1/2">
          <Field
            dataTestId={`${companionGroup}-${index}-birthdate-field`}
            hideErrorMessage
            isRequired
            label={t('prebookingForm.mainTraveler.birthDateInformation')}
            name={`${companionGroup}.${index}.birthdate`}
            placeholder={t('prebookingForm.mainTraveler.birthDateInformation')}
            type="birthdate"
            dayPlaceholder="DD"
            monthPlaceholder="MM"
            yearPlaceholder="YYYY"
            dateFormat={dateFormat}
          />
        </div>
      )}
    </div>
  );
};
